// Check-in screen model (card 524449bc, live stage display Phase 2). PURE.
//
// Check-in follows the stage schedule (Devin 2026-10-07: check-in is what lets
// us show live schedule information): pick a day and stage, then work down that
// stage's running order, competition-round by competition-round. The running
// order and its times come from the schedule screen's own model, so both
// screens always show the same order and clock times. Rounds not on the
// schedule are listed under "Not on the schedule" (an event without a schedule
// has only that list).
import { buildBoard, cardOf, dayLabel, stageTimes } from '../OireachtasPanel/Schedule/oireachtasScheduleModel';

export const UNSCHEDULED = 'unscheduled';
export const progressKey = (compId, round) => `${compId}:${round}`;

// GET /oireachtas/:id/schedule → stations: one per (day, stage) with its running
// order, then the unscheduled rounds. Items: { kind: 'note', key, label, start }
// or { kind: 'round', key, compId, round, card, comp, start } (start in minutes
// after midnight; null when unscheduled). Rows the schedule flags are skipped.
export function buildStations(scheduleRes) {
  const event = scheduleRes?.event || {};
  const b = buildBoard(scheduleRes?.competitions || [], scheduleRes?.schedule, event.days || []);
  const stations = [];
  for (const day of b.days) {
    (b.board[day] || []).forEach((stage, i) => {
      const times = stageTimes(stage, b.cards, b.comps);
      const items = stage.items
        .map((it, j) => {
          const start = times[j] ? times[j].start : null;
          if (it.type === 'note') return { kind: 'note', key: it.key, label: it.label, start };
          if (it.problem) return null;
          return {
            kind: 'round',
            key: progressKey(it.competitionId, it.round),
            compId: it.competitionId,
            round: it.round,
            card: cardOf(it, b.cards),
            comp: b.comps[it.competitionId],
            start,
          };
        })
        .filter(Boolean);
      stations.push({ key: `${day}|${i}`, day, dayLabel: dayLabel(day), name: stage.name, items });
    });
  }
  const pool = b.pool.map((k) => b.cards[k]).filter(Boolean);
  if (pool.length > 0) {
    stations.push({
      key: UNSCHEDULED,
      day: null,
      dayLabel: '',
      name: 'Not on the schedule',
      items: pool.map((card) => ({
        kind: 'round',
        key: progressKey(card.compId, card.round),
        compId: card.compId,
        round: card.round,
        card,
        comp: b.comps[card.compId],
        start: null,
      })),
    });
  }
  return { stations, days: b.days };
}

// GET /oireachtas/:id/tabulation-status → Map progressKey → { drawn, count,
// expected, taken, checkedIn, notComing, frozen } for every competition-round.
// expected = dancers the round must mark: the draw, or in a recall round the
// published recall (null until the recall is published).
export function progressIndex(statusRes) {
  const out = new Map();
  for (const row of statusRes?.rows || []) {
    const compId = String(row.competition?._id);
    for (const rr of row.roundRows || []) {
      const ci = rr.checkIn || {};
      out.set(progressKey(compId, rr.round), {
        drawn: !!row.draw?.locked,
        count: Number(row.draw?.count) || 0,
        taken: !!ci.taken,
        checkedIn: Number(ci.checkedIn) || 0,
        notComing: Number(ci.notComing) || 0,
        frozen: !!ci.frozen,
        expected: ci.expected === undefined ? Number(row.draw?.count) || 0 : ci.expected,
      });
    }
  }
  return out;
}

// One competition-round's state for the running order:
//   nodraw   draw not locked yet (check-in impossible)
//   waiting  a recall round whose recall isn't published yet (nobody to mark)
//   locked   a published recall/result relies on it
//   todo     nobody marked yet
//   partial  some marked, some still to mark
//   done     every dancer due marked (Checked in or No show)
export function itemState(p, round) {
  if (!p || !p.drawn) return 'nodraw';
  if (p.frozen) return 'locked';
  if (round >= 3 && p.expected == null) return 'waiting';
  if (!p.taken) return 'todo';
  if (p.checkedIn + p.notComing >= (p.expected ?? p.count)) return 'done';
  return 'partial';
}

export const STATE_LABEL = {
  nodraw: 'No draw yet',
  waiting: 'Recall not posted',
  locked: 'Locked',
  todo: 'Not started',
  partial: 'In progress',
  done: 'Done',
};

// Where to start on a stage: the first round still to check in (not done,
// not locked, drawn), else the first round.
export function firstOpenIndex(items, progress) {
  const rounds = items.map((it, i) => [it, i]).filter(([it]) => it.kind === 'round');
  const open = rounds.find(([it]) => ['todo', 'partial'].includes(itemState(progress.get(it.key), it.round)));
  if (open) return open[1];
  return rounds.length ? rounds[0][1] : -1;
}

// The next / previous round in the running order (notes skipped), or -1.
export function stepIndex(items, from, dir) {
  for (let i = from + dir; i >= 0 && i < items.length; i += dir) {
    if (items[i].kind === 'round') return i;
  }
  return -1;
}

// Roster filtering: "all" | "blank" | "checked_in" | "no_show", plus a number
// prefix typed into Find (digits only).
export function filterRoster(roster, marks, filter, find) {
  const q = String(find || '').replace(/\D/g, '');
  return (roster || []).filter((row) => {
    const state = marks[row.ref] || null;
    if (filter === 'blank' && state) return false;
    if (filter === 'checked_in' && state !== 'checked_in') return false;
    if (filter === 'no_show' && state !== 'no_show') return false;
    return !q || String(row.number).startsWith(q);
  });
}

export function tally(roster, marks) {
  let checkedIn = 0;
  let notComing = 0;
  for (const row of roster || []) {
    if (marks[row.ref] === 'checked_in') checkedIn += 1;
    else if (marks[row.ref] === 'no_show') notComing += 1;
  }
  return { checkedIn, notComing, blank: (roster || []).length - checkedIn - notComing };
}

// ---- recall-round set (Devin 2026-10-07) -------------------------------------
// Checking a dancer in confirms the set shown; a wrong set is changed first.
// The set line shown on a tile: "The Hunt · 76", "St. Patrick's Day ·
// traditional speed".
export const speedText = (speed) => (String(speed) === 'traditional' ? 'traditional speed' : String(speed || ''));
export const setText = (set) => (set && set.name ? [set.name, speedText(set.speed)].filter(Boolean).join(' · ') : 'No set registered');

// After a change: the set shown, flagged as changed when it differs from the
// registration (same rule as the server's setView).
export function changedSet(set, choice, speed) {
  const reg = (set && set.registered) || {};
  return {
    ...set,
    danceChoice: choice.key,
    name: choice.name,
    speed: String(speed),
    changed: choice.key !== reg.danceChoice || String(speed) !== String(reg.speed),
  };
}

// A tap on the dancer: checked in ↔ unmarked (a No show who turns up is
// checked in). The side button: No show ↔ unmarked.
export const tapState = (current) => (current === 'checked_in' ? null : 'checked_in');
export const notComingState = (current) => (current === 'no_show' ? null : 'no_show');
